import React from 'react';
import { Search, X } from 'lucide-react';
import { MobileHeader as KitMobileHeader } from '@parsina/framework';
import { useTranslation } from '../i18n';

/**
 * The gradient screen header, with the two props the pinned kit lacks.
 *
 * The kit's `MobileHeader` (v0.10.1) already provides both modes this design
 * needs — cockpit (`user`) and content (`onBack` + `title`) — plus `actions`
 * and a `children` context row. It does not provide:
 *
 *   • `search` — the on-gradient field a lookup screen opens into. Every
 *     screen that needed one was otherwise going to paint a white input on the
 *     brand chrome by hand, and at least one of them would have used a
 *     sub-1rem font, which makes iOS Safari zoom the viewport on focus and
 *     takes the layout with it.
 *   • `sticky` — pinning the header so a filter strip in `children` stays
 *     reachable while the list scrolls under it.
 *
 * Both are added here rather than at the call sites so that raising the
 * framework pin deletes this file instead of eight copies.
 * See docs/MOBILE-REDESIGN.md §4.1.
 */
export default function MobileHeader({
  search,
  sticky = false,
  children,
  style,
  ...rest
}) {
  const { t } = useTranslation();

  const context = (search || children) && (
    <>
      {search && <OnGradientSearch {...search} t={t} />}
      {children}
    </>
  );

  return (
    <KitMobileHeader
      {...rest}
      style={{
        ...(sticky ? { position: 'sticky', top: 0, zIndex: 3 } : null),
        ...style,
      }}
    >
      {context}
    </KitMobileHeader>
  );
}

function OnGradientSearch({
  value, onChange, onClear, onSubmit, placeholder, autoFocus, testId, t,
}) {
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  return (
    <form
      role="search"
      onSubmit={(e) => { e.preventDefault(); onSubmit?.(value); }}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        background: 'rgba(255,255,255,0.16)',
        border: '1px solid rgba(255,255,255,0.24)',
        borderRadius: '0.625rem',
        paddingInline: '0.75rem',
        minHeight: '2.75rem',
      }}
    >
      <Search size={16} color="rgba(255,255,255,0.85)" aria-hidden="true" style={{ flexShrink: 0 }} />
      <input
        ref={inputRef}
        type="search"
        value={value || ''}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        data-testid={testId}
        style={{
          flex: 1, minWidth: 0,
          background: 'transparent', border: 'none', outline: 'none',
          color: '#fff',
          // ≥1rem is not a preference. Below it, iOS Safari zooms the viewport
          // on focus and the operator loses the layout they were working in.
          fontSize: '1rem',
          fontFamily: 'inherit',
        }}
      />
      {value ? (
        <button
          type="button"
          onClick={() => onClear?.()}
          aria-label={t('common.actions.clearSearch')}
          style={{
            display: 'grid', placeItems: 'center',
            width: '2rem', height: '2rem', flexShrink: 0,
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'rgba(255,255,255,0.85)',
          }}
        >
          <X size={16} aria-hidden="true" />
        </button>
      ) : null}
    </form>
  );
}
